import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useStripeProducts } from '../context/stripe_products_context';
import Loading from '../components/Loading';
import { Layout } from '../components/page-layout';

const StripeSingleProductPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const { 
    singleStripeProduct, 
    singleStripeProductLoading, 
    singleStripeProductError, 
    fetchSingleStripeProduct 
  } = useStripeProducts();

  useEffect(() => {
    if (productId) {
      fetchSingleStripeProduct(productId);
    }
  }, [productId, fetchSingleStripeProduct]);

  if (singleStripeProductLoading) {
    return <Loading />;
  }

  if (singleStripeProductError || !singleStripeProduct) {
    return (
      <Layout showCommonFooter>
        <Wrapper>
          <div className='section-center'>
            <h2>Product not found</h2>
            <p>The requested product could not be found.</p>
          </div>
        </Wrapper>
      </Layout>
    );
  }

  const { name, description, images, prices, default_price, metadata } = singleStripeProduct;

  // Format price from cents to dollars
  const formatPrice = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  return (
    <Layout showCommonFooter>
      <Wrapper>
        <div className='section-center'>
          <div className='product-center'>
            <div className='product-images'>
              {images && images.length > 0 ? (
                <img src={images[0]} alt={name} className='main-image' />
              ) : (
                <div className='placeholder-image'>No image available</div>
              )}
            </div>
            <div className='product-info'>
              <h1>{name}</h1>
              {description && <p className='description'>{description}</p>}
              
              <div className='pricing-section'>
                <h3>Pricing</h3>
                {default_price && (
                  <div className='default-price'>
                    <span className='price-label'>Default Price:</span>
                    <span className='price-value'>
                      {formatPrice(default_price.unit_amount, default_price.currency)}
                    </span>
                  </div>
                )}
                
                {prices && prices.length > 0 && (
                  <div className='all-prices'>
                    <h4>All Available Prices:</h4>
                    {prices.map((price) => (
                      <div key={price.id} className='price-item'>
                        <span className='price-value'>
                          {formatPrice(price.unit_amount, price.currency)}
                        </span>
                        {price.recurring && (
                          <span className='recurring-info'>
                            / {price.recurring.interval_count} {price.recurring.interval}
                            {price.recurring.interval_count > 1 ? 's' : ''}
                          </span>
                        )}
                        {price.type === 'one_time' && (
                          <span className='price-type'>One-time</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {metadata && Object.keys(metadata).length > 0 && (
                <div className='metadata-section'>
                  <h3>Additional Information</h3>
                  <div className='metadata-grid'>
                    {Object.entries(metadata).map(([key, value]) => (
                      <div key={key} className='metadata-item'>
                        <span className='metadata-key'>{key}:</span>
                        <span className='metadata-value'>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className='product-actions'>
                <button className='btn primary-btn'>
                  Add to Cart
                </button>
                <button className='btn secondary-btn'>
                  View All Products
                </button>
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
    </Layout>
  );
};

const Wrapper = styled.section`
  .section-center {
    width: 90vw;
    margin: 0 auto;
    max-width: 1170px;
    padding: 2rem 0;
  }
  
  .product-center {
    display: grid;
    gap: 4rem;
    margin-top: 2rem;
  }
  
  @media (min-width: 992px) {
    .product-center {
      grid-template-columns: 1fr 1fr;
      align-items: start;
    }
  }
  
  .product-images {
    .main-image {
      width: 100%;
      height: 400px;
      object-fit: cover;
      border-radius: var(--radius);
    }
    
    .placeholder-image {
      width: 100%;
      height: 400px;
      background: var(--clr-grey-10);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius);
      color: var(--clr-grey-5);
    }
  }
  
  .product-info {
    h1 {
      color: var(--clr-grey-1);
      margin-bottom: 1rem;
    }
    
    .description {
      color: var(--clr-grey-5);
      line-height: 2;
      margin-bottom: 2rem;
    }
  }
  
  .pricing-section {
    margin-bottom: 2rem;
    
    h3 {
      color: var(--clr-grey-1);
      margin-bottom: 1rem;
    }
    
    .default-price {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 1rem;
      
      .price-label {
        font-weight: 600;
        color: var(--clr-grey-3);
      }
      
      .price-value {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--clr-primary-5);
      }
    }
    
    .all-prices {
      h4 {
        color: var(--clr-grey-3);
        margin-bottom: 0.5rem;
      }
      
      .price-item {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 0.5rem;
        
        .price-value {
          font-weight: 600;
          color: var(--clr-grey-1);
        }
        
        .recurring-info {
          color: var(--clr-grey-5);
          font-size: 0.875rem;
        }
        
        .price-type {
          background: var(--clr-primary-10);
          color: var(--clr-primary-1);
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius);
          font-size: 0.75rem;
        }
      }
    }
  }
  
  .metadata-section {
    margin-bottom: 2rem;
    
    h3 {
      color: var(--clr-grey-1);
      margin-bottom: 1rem;
    }
    
    .metadata-grid {
      display: grid;
      gap: 0.5rem;
    }
    
    .metadata-item {
      display: flex;
      gap: 1rem;
      
      .metadata-key {
        font-weight: 600;
        color: var(--clr-grey-3);
        min-width: 120px;
      }
      
      .metadata-value {
        color: var(--clr-grey-5);
      }
    }
  }
  
  .product-actions {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    
    .btn {
      padding: 0.75rem 1.5rem;
      border-radius: var(--radius);
      border: none;
      cursor: pointer;
      font-weight: 600;
      transition: var(--transition);
    }
    
    .primary-btn {
      background: var(--clr-primary-5);
      color: var(--clr-white);
      
      &:hover {
        background: var(--clr-primary-1);
      }
    }
    
    .secondary-btn {
      background: var(--clr-grey-10);
      color: var(--clr-grey-1);
      
      &:hover {
        background: var(--clr-grey-5);
        color: var(--clr-white);
      }
    }
  }
`;

export default StripeSingleProductPage; 